import { useMemo } from "react";
import { Link } from "react-router-dom";
import SiteHeader from "@/components/feature/SiteHeader";
import SiteFooter from "@/components/feature/SiteFooter";
import Breadcrumbs from "@/components/feature/Breadcrumbs";
import BackToTop from "@/components/feature/BackToTop";
import SectionHeading from "@/components/feature/SectionHeading";
import { useSeo } from "@/hooks/useSeo";
import { site } from "@/mocks/site";

const doList = [
  "登记每一份材料的来源与取得方式",
  "把爆料拆成最小事实单元逐条核对",
  "公开支撑结论的材料清单与核验状态",
  "在证据不足时明确写『待求证』",
];

const dontList = [
  "不把热度当作可信度的依据",
  "不因为一部分成立就为整条爆料背书",
  "不发布涉及个人隐私的未脱敏信息",
  "不在没有材料的情况下给出确定性结论",
];

export default function AboutPage() {
  const jsonLd = useMemo(
    () => ({
      "@context": "https://schema.org",
      "@type": "AboutPage",
      name: "关于我们｜拾真社",
      url: "https://hdlyjgs.cn/about",
      inLanguage: "zh-CN",
      description: site.intro,
      mainEntity: {
        "@type": "Organization",
        name: "拾真社",
        email: site.email,
        description: "拾真社是专注独家爆料求证的内容平台，逐条整理实锤证据与原始来源，提供可信度辨析与真假对照。",
      },
    }),
    [],
  );

  useSeo({
    title: "关于我们｜独家爆料求证平台的定位与内容原则 · 拾真社",
    description:
      "拾真社是专注独家爆料求证的内容平台，坚持先看材料再看说法，公开来源链与可信度评分。了解我们的定位、内容原则与用户口碑。",
    keywords: "拾真社,独家爆料求证平台,内容原则,信息来源核实",
    path: "/about",
    jsonLd,
  });

  return (
    <div className="min-h-screen bg-background-50">
      <SiteHeader activePath="/about" />
      <Breadcrumbs items={[{ label: "关于我们" }]} />

      <section className="border-b border-background-200 bg-background-100">
        <div className="mx-auto grid max-w-[1200px] grid-cols-1 items-center gap-10 px-4 py-12 md:px-8 md:py-16 lg:grid-cols-[1.2fr_1fr]">
          <div>
            <p className="flex items-center gap-2 font-label text-[11px] uppercase tracking-[0.2em] text-primary-600">
              <span className="inline-block h-px w-6 bg-primary-400" aria-hidden="true"></span>
              关于拾真社
            </p>
            <h1 className="mt-5 font-heading text-3xl font-bold leading-snug text-foreground-950 md:text-4xl">
              在信息翻涌的时候，做那个愿意多核一遍的人
            </h1>
            <p className="mt-5 max-w-2xl text-sm leading-relaxed text-foreground-700 md:text-base">
              {site.intro}
            </p>
            <p className="mt-4 max-w-2xl text-sm leading-relaxed text-foreground-600">
              我们不追求第一时间发出最刺激的标题，而是希望每一条发出去的内容，都能经得起读者沿着来源链往回走一遍。
              这也是「拾真」这个名字的由来：把散落的真相碎片一片片捡回来。
            </p>
            <div className="mt-8 flex flex-col gap-3 sm:flex-row">
              <Link
                to="/scoops"
                className="inline-flex items-center justify-center gap-2 rounded-md bg-primary-500 px-5 py-2.5 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600 whitespace-nowrap"
              >
                <i className="ri-folder-open-line" aria-hidden="true"></i>
                看看我们的档案
              </Link>
              <Link
                to="/contact"
                className="inline-flex items-center justify-center gap-2 rounded-md border border-foreground-300 px-5 py-2.5 text-sm text-foreground-800 transition-colors hover:border-primary-400 hover:text-primary-600 whitespace-nowrap"
              >
                联系我们
              </Link>
            </div>
          </div>

          <div className="relative w-full h-64 overflow-hidden rounded-lg border border-background-200 md:h-80">
            <img
              src="https://readdy.ai/api/search-image?query=Warm%20editorial%20photo%20of%20a%20calm%20verification%20workspace%20with%20open%20folders%2C%20sticky%20notes%20and%20a%20magnifier%20on%20a%20wooden%20desk%2C%20soft%20morning%20light%2C%20cream%20and%20amber%20palette%2C%20documentary%20minimal%20composition&width=900&height=700&seq=about-studio&orientation=portrait"
              alt="拾真社求证工作台，桌面摆放整理中的档案与放大镜"
              title="拾真社 · 求证工作日常"
              className="absolute inset-0 h-full w-full object-cover object-top"
            />
          </div>
        </div>
      </section>

      <main className="mx-auto max-w-[1200px] px-4 py-12 md:px-8 md:py-16">
        <section aria-labelledby="position-heading" className="mb-16">
          <div id="position-heading">
            <SectionHeading
              eyebrow="网站定位"
              title="我们做的，和我们不做的"
              desc="把边界说清楚，比把口号喊响亮更有用。"
            />
          </div>
          <div className="grid grid-cols-1 gap-5 md:grid-cols-2">
            <div className="rounded-lg border border-secondary-200 bg-secondary-50 p-6">
              <h3 className="flex items-center gap-2 font-heading text-lg font-bold text-secondary-900">
                <i className="ri-checkbox-circle-line text-xl" aria-hidden="true"></i>
                我们会做
              </h3>
              <ul className="mt-5 space-y-3">
                {doList.map((text) => (
                  <li key={text} className="flex items-start gap-3 text-sm leading-relaxed text-secondary-900">
                    <span className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center text-secondary-600">
                      <i className="ri-check-line" aria-hidden="true"></i>
                    </span>
                    {text}
                  </li>
                ))}
              </ul>
            </div>
            <div className="rounded-lg border border-background-300 bg-background-100 p-6">
              <h3 className="flex items-center gap-2 font-heading text-lg font-bold text-foreground-950">
                <i className="ri-forbid-2-line text-xl text-primary-500" aria-hidden="true"></i>
                我们不做
              </h3>
              <ul className="mt-5 space-y-3">
                {dontList.map((text) => (
                  <li key={text} className="flex items-start gap-3 text-sm leading-relaxed text-foreground-700">
                    <span className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center text-primary-500">
                      <i className="ri-close-line" aria-hidden="true"></i>
                    </span>
                    {text}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </section>

        <section aria-labelledby="values-heading" className="mb-16">
          <div id="values-heading">
            <SectionHeading
              eyebrow="发展理念"
              title="求证是一件慢下来的事"
              desc="在人人抢着转发的环境里，慢一点反而更接近事实。"
            />
          </div>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
            {site.stats.map((stat) => (
              <div key={stat.label} className="rounded-lg border border-background-200 bg-background-50 p-6 text-center">
                <p className="font-heading text-3xl font-bold text-primary-600">{stat.value}</p>
                <p className="mt-2 text-[13px] text-foreground-600">{stat.label}</p>
              </div>
            ))}
          </div>
          <div className="mt-6 grid grid-cols-1 gap-4 md:grid-cols-2">
            <p className="rounded-lg border border-background-200 bg-background-50 p-5 text-sm leading-relaxed text-foreground-700">
              <span className="font-heading font-bold text-foreground-950">关于内容原则：</span>
              所有档案只描述我们能够核到的部分。对无法验证的传闻，我们宁可标注为待求证，也不会用推测把它补完整。
            </p>
            <p className="rounded-lg border border-background-200 bg-background-50 p-5 text-sm leading-relaxed text-foreground-700">
              <span className="font-heading font-bold text-foreground-950">关于更正：</span>
              如果发现已发布档案有误，我们会直接修改并标注更新时间，而不是悄悄替换内容，读者可以看到变化。
            </p>
          </div>
        </section>

        <section aria-labelledby="voice-heading" className="mb-16">
          <div id="voice-heading">
            <SectionHeading
              eyebrow="用户口碑"
              title="读者怎么看我们"
              desc="来自长期使用档案库的读者与从业者的反馈，仅代表个人观点。"
            />
          </div>
          <ul className="grid grid-cols-1 gap-4 md:grid-cols-3">
            {site.testimonials.map((item) => (
              <li key={item.name} className="rounded-lg border border-background-200 bg-background-50 p-6">
                <div className="flex items-center gap-1 text-accent-500">
                  {Array.from({ length: 5 }).map((_, index) => (
                    <i key={index} className="ri-star-fill text-sm" aria-hidden="true"></i>
                  ))}
                </div>
                <p className="mt-4 text-sm leading-relaxed text-foreground-700">{item.text}</p>
                <p className="mt-4 border-t border-background-200 pt-4 font-heading text-[13px] font-bold text-foreground-950">
                  {item.name}
                  <span className="ml-2 font-body font-normal text-foreground-500">{item.role}</span>
                </p>
              </li>
            ))}
          </ul>
        </section>

        <section className="overflow-hidden rounded-lg border border-background-200 bg-background-100">
          <div className="grid grid-cols-1 gap-6 p-6 md:grid-cols-3 md:p-10">
            <div className="md:col-span-1">
              <h2 className="font-heading text-xl font-bold text-foreground-950">联系方式</h2>
              <p className="mt-3 text-sm leading-relaxed text-foreground-600">
                投稿、纠错、合作都可以通过下面的方式找到我们。
              </p>
            </div>
            <dl className="grid grid-cols-1 gap-4 sm:grid-cols-3 md:col-span-2">
              <div className="rounded-lg border border-background-200 bg-background-50 p-4">
                <dt className="font-label text-[11px] uppercase tracking-widest text-foreground-500">投稿线索</dt>
                <dd className="mt-2 text-sm font-medium text-foreground-950 break-all">{site.tipEmail}</dd>
              </div>
              <div className="rounded-lg border border-background-200 bg-background-50 p-4">
                <dt className="font-label text-[11px] uppercase tracking-widest text-foreground-500">合作与纠错</dt>
                <dd className="mt-2 text-sm font-medium text-foreground-950 break-all">{site.email}</dd>
              </div>
              <div className="rounded-lg border border-background-200 bg-background-50 p-4">
                <dt className="font-label text-[11px] uppercase tracking-widest text-foreground-500">工作时段</dt>
                <dd className="mt-2 text-sm font-medium text-foreground-950">周一至周五 10:00–19:00</dd>
              </div>
            </dl>
          </div>
        </section>
      </main>
      <SiteFooter />
      <BackToTop />
    </div>
  );
}